import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Box, Typography, Chip, CircularProgress, Button,
  Table, TableHead, TableBody, TableRow, TableCell, Paper,
  TextField, InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  FileDownload as DownloadIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  dreService, DreDrilldownRow, DreSummaryRowKind, DreRegime, formatBRL,
  dreMonthBoundsFromBucket,
} from '../../services/dreService';

interface Props {
  open: boolean;
  onClose: () => void;
  rowKind?: DreSummaryRowKind;
  accountId?: string;
  /** Todas as contas DRE com o mesmo código (após merge na tabela); preferir a lista inteira */
  accountIds?: string[];
  bucket?: string;        // 'YYYY-MM-01'
  unitIds?: string[];
  regime: DreRegime;
  rowLabel?: string;
}

const SOURCE_LABELS: Record<string, string> = {
  transaction: 'Transação',
  fixed_expense: 'Despesa fixa',
  fixed_revenue: 'Receita fixa',
  inventory_consumption: 'Consumo de estoque',
  commission: 'Comissão',
  professional_fee: 'Repasse',
  card_fee: 'Taxa de cartão',
  lab_payment: 'Laboratório',
  nfse_iss: 'ISS NFSe',
};

const SOURCE_COLORS: Record<string, string> = {
  transaction: '#0EA5E9',
  fixed_expense: '#A855F7',
  fixed_revenue: '#22C55E',
  inventory_consumption: '#F59E0B',
  commission: '#F472B6',
  professional_fee: '#EC4899',
  card_fee: '#FB923C',
  lab_payment: '#06B6D4',
  nfse_iss: '#94A3B8',
};

export const DreDrilldownDialog: React.FC<Props> = ({
  open, onClose, rowKind, accountId, accountIds, bucket, unitIds, regime, rowLabel,
}) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DreDrilldownRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const { periodStart, periodEnd } = React.useMemo(
    () => dreMonthBoundsFromBucket(bucket || ''),
    [bucket],
  );

  const resolvedAccountIds = React.useMemo(() => {
    if (accountIds && accountIds.length > 0) return accountIds;
    if (accountId) return [accountId];
    return [];
  }, [accountIds, accountId]);

  useEffect(() => {
    if (!open || !bucket || !periodStart || !periodEnd) return;
    setLoading(true);
    setError(null);
    const fetcher = resolvedAccountIds.length > 0
      ? dreService.drilldownAccount({
        accountIds: resolvedAccountIds,
        periodStart,
        periodEnd,
        unitIds,
        regime,
      })
      : rowKind
      ? dreService.drilldownSummary({ rowKind, periodStart, periodEnd, unitIds, regime })
      : Promise.resolve([]);

    fetcher
      .then(setRows)
      .catch((e: any) => setError(e?.message || 'Erro ao carregar lançamentos'))
      .finally(() => setLoading(false));
  }, [open, resolvedAccountIds, rowKind, bucket, unitIds, regime, periodStart, periodEnd]);

  const filtered = React.useMemo(() => {
    if (!search.trim()) return rows;
    const s = search.toLowerCase();
    return rows.filter((r) =>
      [r.description, r.category, r.source, r.payment_method, r.account_name,
        r.patient_name, r.professional_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(s))
    );
  }, [rows, search]);

  const total = filtered.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  const exportCsv = () => {
    const header = [
      'Origem', 'Descrição', 'Categoria', 'Conta DRE', 'Paciente', 'Profissional',
      'Data competência', 'Data caixa', 'Valor', 'Status', 'Forma pagamento',
    ];
    const lines = filtered.map((r) => [
      SOURCE_LABELS[r.source] || r.source,
      r.description || '',
      r.category || '',
      r.account_name ? `${r.account_code || ''} ${r.account_name}` : '',
      r.patient_name || '',
      r.professional_name || '',
      r.competence_date || '',
      r.cash_date || '',
      r.amount,
      r.status,
      r.payment_method || '',
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const blob = new Blob(['\uFEFF' + [header.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dre-drilldown-${bucket || 'periodo'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { borderRadius: 3, height: '85vh' } }}>
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #0C6FC1 0%, #0a5da0 100%)',
        color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5,
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700 }}>
            Drill-down · {bucket && new Date(bucket).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
            {rowLabel || 'Lançamentos'}
          </Typography>
        </Box>
        <Chip
          label={`${rows.length} lançamento${rows.length !== 1 ? 's' : ''}`}
          size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700 }}
        />
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 2, p: 2, borderBottom: '1px solid #e2e8f0',
        }}>
          <TextField
            placeholder="Buscar lançamento…"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
            sx={{ flex: 1, maxWidth: 320 }}
          />
          <Box sx={{ flex: 1 }} />
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" sx={{ color: '#64748b' }}>Total</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: total < 0 ? '#dc2626' : '#0f172a' }}>
              {formatBRL(total)}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportCsv}
            disabled={filtered.length === 0}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            CSV
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
              <CircularProgress size={32} sx={{ color: '#0C6FC1' }} />
            </Box>
          ) : error ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error">{error}</Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">Nenhum lançamento encontrado.</Typography>
            </Box>
          ) : (
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 800, fontSize: '0.7rem', color: '#475569', bgcolor: '#f8fafc', textTransform: 'uppercase', letterSpacing: 0.5 } }}>
                  <TableCell>Origem</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Conta DRE</TableCell>
                  <TableCell>Paciente</TableCell>
                  <TableCell>Profissional</TableCell>
                  <TableCell>Competência</TableCell>
                  <TableCell>Caixa</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow
                    key={`${r.source}-${r.source_id}`}
                    sx={{ '&:hover': { bgcolor: '#f8fafc' } }}
                  >
                    <TableCell>
                      <Chip
                        label={SOURCE_LABELS[r.source] || r.source}
                        size="small"
                        sx={{
                          bgcolor: `${SOURCE_COLORS[r.source] || '#64748b'}15`,
                          color: SOURCE_COLORS[r.source] || '#64748b',
                          fontWeight: 700, fontSize: '0.65rem', height: 20,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: '#0f172a' }} noWrap>
                        {r.description || '—'}
                      </Typography>
                      {r.category && (
                        <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>
                          {r.category}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: '#475569', maxWidth: 160 }}>
                      <Typography noWrap sx={{ fontSize: 'inherit' }}>
                        {r.account_code ? `${r.account_code} · ${r.account_name}` : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', color: '#475569', maxWidth: 160 }}>
                      <Typography noWrap sx={{ fontSize: 'inherit' }} title={r.patient_name || undefined}>
                        {r.patient_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', color: '#475569', maxWidth: 140 }}>
                      <Typography noWrap sx={{ fontSize: 'inherit' }} title={r.professional_name || undefined}>
                        {r.professional_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', color: '#475569', whiteSpace: 'nowrap' }}>
                      {r.competence_date ? new Date(r.competence_date).toLocaleDateString('pt-BR') : '—'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.72rem', color: '#475569', whiteSpace: 'nowrap' }}>
                      {r.cash_date ? new Date(r.cash_date).toLocaleDateString('pt-BR') : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={r.status}
                        size="small"
                        sx={{
                          fontSize: '0.65rem', fontWeight: 700, height: 20, textTransform: 'uppercase',
                          bgcolor: ['paid','authorized','settled'].includes(r.status) ? '#dcfce7' :
                                   ['pending','expected'].includes(r.status) ? '#fef3c7' :
                                   '#fee2e2',
                          color: ['paid','authorized','settled'].includes(r.status) ? '#15803d' :
                                 ['pending','expected'].includes(r.status) ? '#854d0e' :
                                 '#991b1b',
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontFamily: '"JetBrains Mono", monospace', fontWeight: 700,
                      color: r.amount < 0 ? '#dc2626' : '#0f172a',
                      whiteSpace: 'nowrap',
                    }}>
                      {formatBRL(r.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e2e8f0', px: 2, py: 1 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: 'none', borderRadius: 2 }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DreDrilldownDialog;
